import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import api from "../../api";
import AiBillingCard from "./AiBillingCard";
import { Card } from "../ui/CCard";
import {
  CreditCard, Sparkles, Receipt, Calendar, Users, CheckCircle2,
  AlertTriangle, Download, Zap, Loader2, X, FileText, BarChart3,
  Video, Stethoscope,
} from "lucide-react";

// Relocated from Settings.js's old "Subscription" tab (same state/handlers,
// unchanged) into its own component under Finance > Subscription - that
// page is admin-only (see FinanceHome.js), matching the backend's own
// admin-only gate on every mutating subscription route (/pay,
// /addon/activate, /addon/deactivate, /verify). GET .../subscription itself
// is not admin-restricted server-side, but this screen never renders for a
// non-admin at all.

const PAYMENT_PURPOSE_LABELS = {
  NEW_SUBSCRIPTION: "New Subscription",
  RENEWAL: "Renewal",
  ADDON_ACTIVATION: "AI Add-on",
};

const STATUS_STYLES = {
  SUCCESS: "bg-emerald-100 text-emerald-700",
  PENDING: "bg-amber-100 text-amber-700",
  FAILED: "bg-red-100 text-red-700",
};

// What the AI add-on actually does, in terms a doctor/therapist cares about
// (not just "AI features") - shown both on the card and in the activation
// confirmation, so paying for it is an informed decision, not a leap of faith.
const AI_FEATURE_BENEFITS = [
  { icon: Video, text: "Record sessions automatically for later review, supervision, or training" },
  { icon: FileText, text: "AI-generated session summaries after every consultation - no manual note-writing" },
  { icon: BarChart3, text: "Benchmark responses against expert clinical protocols (CBT/REBT/SFBT) to see what worked and what to improve" },
  { icon: Stethoscope, text: "AI-assisted diagnosis from reported symptoms - suggests likely conditions, recommended tests, medicine, and what the patient should do next, reviewed and finalized by the doctor before it's sent" },
];

const money = (n) => `₹${Number(n || 0).toFixed(2)}`;

// 'half_yearly' -> 'half-yearly' for display.
const fmtCycle = (c) => String(c || "").replace(/_/g, "-");

function ConfirmModal({ title, icon: Icon, iconClass, onCancel, onConfirm, confirmLabel, confirmBusy, children }) {
  // Portaled straight to document.body (same pattern AdminOrganizations.js's
  // row-actions menu already uses) - this component renders nested inside
  // an `animate-fade-in-up` ancestor, and a CSS animation/transform on any
  // ancestor becomes the containing block for `position: fixed`
  // descendants, so without a portal this modal (and its overlay) would
  // center and size itself relative to that ancestor's box instead of the
  // real viewport - exactly the "modal isn't centered on the page, overlay
  // only covers part of the screen" bug this fixes.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-secondary-100">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${iconClass}`}>
              <Icon className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-bold text-secondary-900">{title}</h3>
          </div>
          <button onClick={onCancel} className="p-1.5 rounded-full hover:bg-secondary-100 text-secondary-400">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">{children}</div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-secondary-100 bg-secondary-50/50">
          <button onClick={onCancel} className="px-4 py-2 border border-secondary-200 rounded-lg text-secondary-700 bg-white hover:bg-secondary-50 font-medium text-sm">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={confirmBusy}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg font-semibold text-sm"
          >
            {confirmBusy && <Loader2 className="w-4 h-4 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function SubscriptionBilling() {
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [subscriptionPayments, setSubscriptionPayments] = useState([]);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [addonQuote, setAddonQuote] = useState(null);
  const [addonQuoteLoading, setAddonQuoteLoading] = useState(false);
  const [subscriptionActionBusy, setSubscriptionActionBusy] = useState(false);
  const [showRenewConfirm, setShowRenewConfirm] = useState(false);
  const [showAddonConfirm, setShowAddonConfirm] = useState(false);
  // Only relevant when the add-on isn't already on - lets an admin bundle
  // "enable AI" into this renewal's payment instead of using the separate
  // prorated "Enable Now" flow. The backend defers actually turning it on
  // until the new cycle really starts if this renewal is paid early (see
  // ai_addon_activation_date in the /pay response below) - bundling never
  // grants AI usage for days that were only paid for at the base rate.
  const [bundleAiAddonOnRenew, setBundleAiAddonOnRenew] = useState(false);
  // Free-trial conversion only: how the doctor wants to pay for AI after the
  // trial - the monthly add-on, or per AI session taken from payouts.
  const [aiOption, setAiOption] = useState("addon");
  // Self-service billing-cycle switch, chosen in the renew dialog. Takes
  // effect with that renewal (never mid-cycle); any cycle other than the
  // current one is priced from the catalog (per seat x seat limit) - see
  // subscription.py's /cycle-options and /pay.
  const [cycleOptions, setCycleOptions] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null);

  useEffect(() => {
    setSubscriptionLoading(true);
    Promise.all([api.fetchSubscription(), api.fetchSubscriptionPayments()])
      .then(([subRes, paymentsRes]) => {
        setSubscriptionData(subRes.data);
        setSubscriptionPayments(paymentsRes.data || []);
      })
      .catch(() => toast.error("Could not load subscription details."))
      .finally(() => setSubscriptionLoading(false));
  }, []);

  const refreshSubscription = async () => {
    try {
      const [subRes, paymentsRes] = await Promise.all([api.fetchSubscription(), api.fetchSubscriptionPayments()]);
      setSubscriptionData(subRes.data);
      setSubscriptionPayments(paymentsRes.data || []);
    } catch {
      // toast already shown by the axios interceptor on failure
    }
  };

  const doRenewPayment = async () => {
    setSubscriptionActionBusy(true);
    try {
      const { loadRazorpay } = await import("../../utils/loadRazorpay");
      const orderRes = await api.createSubscriptionOrder({
        enable_ai_addon: bundleAiAddonOnRenew,
        ...(isSwitchingCycle ? { billing_cycle: selectedCycle } : {}),
        ...(subscriptionData.is_trial ? { ai_option: aiOption, billing_cycle: selectedCycle } : {}),
      });
      const { payment_id, gateway_order_id, amount, key_id, ai_addon_activation_date, new_billing_cycle } = orderRes.data;
      const ready = await loadRazorpay();
      if (!ready || !key_id) {
        toast.error("Payment gateway is not available right now.");
        setSubscriptionActionBusy(false);
        return;
      }
      const rzp = new window.Razorpay({
        key: key_id,
        order_id: gateway_order_id,
        amount: Math.round(amount * 100),
        currency: "INR",
        name: "Subscription Renewal",
        handler: async (response) => {
          try {
            await api.verifySubscriptionPayment({
              payment_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success(
              ai_addon_activation_date
                ? `Subscription renewed! AI Features will activate automatically on ${ai_addon_activation_date}.`
                : new_billing_cycle
                  ? `Subscription renewed - you're now on ${fmtCycle(new_billing_cycle)} billing.`
                  : "Subscription renewed successfully!"
            );
            refreshSubscription();
          } catch {
            toast.error("Payment succeeded but could not be verified. Please contact support.");
          }
        },
        modal: { ondismiss: () => setSubscriptionActionBusy(false) },
      });
      setShowRenewConfirm(false);
      setBundleAiAddonOnRenew(false);
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Could not start payment.");
    } finally {
      setSubscriptionActionBusy(false);
    }
  };

  const openRenewDialog = async () => {
    setSelectedCycle(subscriptionData.billing_cycle);
    setShowRenewConfirm(true);
    try {
      const res = await api.fetchSubscriptionCycleOptions();
      setCycleOptions(res.data || []);
    } catch {
      setCycleOptions([]); // renew-as-is still works without the picker
    }
  };

  const handleOpenEnableAddon = async () => {
    setAddonQuoteLoading(true);
    try {
      const res = await api.fetchAddonQuote();
      setAddonQuote(res.data);
      setShowAddonConfirm(true);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Could not fetch add-on pricing.");
    } finally {
      setAddonQuoteLoading(false);
    }
  };

  const doActivateAddon = async () => {
    setSubscriptionActionBusy(true);
    try {
      const { loadRazorpay } = await import("../../utils/loadRazorpay");
      const orderRes = await api.activateAddon();
      const { payment_id, gateway_order_id, amount, key_id } = orderRes.data;
      const ready = await loadRazorpay();
      if (!ready || !key_id) {
        toast.error("Payment gateway is not available right now.");
        setSubscriptionActionBusy(false);
        return;
      }
      const rzp = new window.Razorpay({
        key: key_id,
        order_id: gateway_order_id,
        amount: Math.round(amount * 100),
        currency: "INR",
        name: "AI Features Add-on",
        handler: async (response) => {
          try {
            await api.verifySubscriptionPayment({
              payment_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("AI features enabled!");
            setAddonQuote(null);
            refreshSubscription();
          } catch {
            toast.error("Payment succeeded but could not be verified. Please contact support.");
          }
        },
        modal: { ondismiss: () => setSubscriptionActionBusy(false) },
      });
      setShowAddonConfirm(false);
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Could not start add-on activation.");
    } finally {
      setSubscriptionActionBusy(false);
    }
  };

  const handleDeactivateAddon = async () => {
    if (!window.confirm("Disable AI features? Everyone's remaining AI session credits will be zeroed immediately.")) return;
    setSubscriptionActionBusy(true);
    try {
      await api.deactivateAddon();
      toast.success("AI features disabled.");
      refreshSubscription();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Could not disable add-on.");
    } finally {
      setSubscriptionActionBusy(false);
    }
  };

  const handleViewInvoice = async (paymentId) => {
    try {
      const res = await api.fetchSubscriptionInvoiceUrl(paymentId);
      window.open(res.data.url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Invoice not available.");
    }
  };

  if (subscriptionLoading) {
    return (
      <Card className="p-12 flex flex-col items-center justify-center text-secondary-400">
        <Loader2 className="w-6 h-6 animate-spin mb-2" />
        Loading subscription details...
      </Card>
    );
  }

  // The platform org (NeuralLoom Labs itself) needs no subscription.
  if (subscriptionData?.is_platform_org) {
    return (
      <Card className="p-12 text-center">
        <div className="w-14 h-14 rounded-2xl bg-secondary-100 flex items-center justify-center mx-auto mb-4">
          <CreditCard className="w-7 h-7 text-secondary-400" />
        </div>
        <h3 className="text-lg font-bold text-secondary-900 mb-1">No Subscription Needed</h3>
        <p className="text-secondary-500 max-w-sm mx-auto">This is the platform administration organisation, so it doesn't need a subscription.</p>
      </Card>
    );
  }

  if (!subscriptionData?.has_subscription) {
    return (
      <Card className="p-12 text-center">
        <div className="w-14 h-14 rounded-2xl bg-secondary-100 flex items-center justify-center mx-auto mb-4">
          <CreditCard className="w-7 h-7 text-secondary-400" />
        </div>
        <h3 className="text-lg font-bold text-secondary-900 mb-1">No Subscription Yet</h3>
        <p className="text-secondary-500 max-w-sm mx-auto">No subscription is set up for your organization yet. Contact support to get started.</p>
      </Card>
    );
  }

  const seatPct = subscriptionData.seat_limit
    ? Math.min(100, Math.round((subscriptionData.seats_used / subscriptionData.seat_limit) * 100))
    : null;
  const isActive = subscriptionData.status === "active";
  // Pay-per-AI-session (set by NL Labs) is AI too: show it as active and never
  // offer the monthly add-on on top, which would bill the clinic twice.
  const perSessionAi = subscriptionData.ai_billing_mode === "per_session";
  const aiActive = subscriptionData.ai_addon_enabled || perSessionAi;
  // The ongoing per-cycle total once the add-on is active - known even
  // before activation, since ai_addon_price_per_cycle is snapshotted on the
  // subscription regardless of ai_addon_enabled (set by the platform admin
  // when the subscription was assigned).
  const addonOngoingPrice = subscriptionData.ai_addon_price_per_cycle;
  const ongoingTotalWithAddon = addonOngoingPrice != null
    ? Number(subscriptionData.base_price_per_cycle || 0) + Number(addonOngoingPrice)
    : null;
  const pendingAddonDate = subscriptionData.pending_ai_addon_activation_date;
  // Preview of this renewal's new cycle start, same max(today, end_date)
  // rule the backend applies - lets the checkbox explain up front whether
  // bundling AI here will activate it immediately or defer it, before the
  // admin even submits the payment.
  // A renewal starts after the last period already paid for (paid_until).
  const todayISO = new Date().toISOString().slice(0, 10);
  const paidUntil = subscriptionData.paid_until || subscriptionData.end_date;
  const renewalCycleStart = paidUntil && paidUntil > todayISO ? paidUntil : todayISO;
  const upcomingPeriods = subscriptionData.upcoming_periods || [];
  const bundleWillDefer = bundleAiAddonOnRenew && renewalCycleStart > todayISO;
  const selectedOption = cycleOptions.find((o) => o.billing_cycle === selectedCycle);
  const isSwitchingCycle = !!selectedOption && !selectedOption.is_current;
  // Prices for the renewal being paid: the chosen cycle's (catalog) prices
  // when switching, otherwise the org's own current prices.
  const renewBase = isSwitchingCycle ? selectedOption.base_price : Number(subscriptionData.base_price_per_cycle || 0);
  const renewAddon = isSwitchingCycle ? selectedOption.ai_addon_price : subscriptionData.ai_addon_price_per_cycle;
  const includesAddon = subscriptionData.is_trial
    ? aiOption === "addon"
    : (subscriptionData.ai_addon_enabled || bundleAiAddonOnRenew);
  const addonUnavailable = isSwitchingCycle && includesAddon && renewAddon == null;
  const renewAmountWithBundle = Number(renewBase || 0) + (includesAddon ? Number(renewAddon || 0) : 0);

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Free trial - ends on trial_end; picking a plan starts it then */}
      {subscriptionData.is_trial && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary-200 bg-primary-50 px-4 py-3">
          <p className="text-sm text-primary-900">
            <span className="font-semibold">You're on a free trial</span> until {subscriptionData.trial_end}
            {subscriptionData.trial_ai_sessions_per_day ? `, with up to ${subscriptionData.trial_ai_sessions_per_day} free AI sessions a day` : ""}.
            {" "}Choose a plan to keep everything running after that - your paid plan starts when the trial ends.
          </p>
          <button
            type="button"
            onClick={openRenewDialog}
            className="shrink-0 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold px-4 py-2 rounded-lg"
          >
            Choose a plan
          </button>
        </div>
      )}

      {/* Per-session AI usage and dues - renders nothing for monthly-AI orgs */}
      <AiBillingCard />

      {/* Expiry alert - shown above everything when relevant, same urgency
          cue as the global DashboardLayout banner. */}
      {subscriptionData.show_expiry_warning && (
        <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800 font-medium">
            Your subscription ends on {subscriptionData.end_date} - {subscriptionData.days_remaining} day{subscriptionData.days_remaining === 1 ? "" : "s"} remaining.
          </p>
        </div>
      )}

      {/* Current Plan */}
      <Card className="p-6 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-48 h-48 bg-primary-50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        {/* flex-wrap (not a md:/lg: viewport breakpoint) so this reflows off
            the card's own actual rendered width - this card sits behind two
            nested sidebars (the global dashboard nav + this Finance page's
            own in-page nav), so a viewport-width breakpoint would decide
            "go horizontal" well before there's really room for it, squeezing
            everything instead of wrapping cleanly. */}
        <div className="relative flex flex-wrap items-center justify-between gap-5">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-12 h-12 rounded-2xl bg-primary-100 flex items-center justify-center shrink-0">
              <CreditCard className="w-6 h-6 text-primary-600" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-secondary-400">Current Plan</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${isActive ? "bg-emerald-100 text-emerald-700" : "bg-secondary-100 text-secondary-600"}`}>
                  {isActive ? "Active" : subscriptionData.status}
                </span>
              </div>
              <div className="text-3xl font-heading font-bold text-secondary-900">
                ₹{subscriptionData.current_price_per_cycle?.toFixed(2)}
                <span className="text-base font-medium text-secondary-400"> / {fmtCycle(subscriptionData.billing_cycle)}</span>
              </div>
              {subscriptionData.ai_addon_enabled && (
                <p className="text-xs text-secondary-400 mt-0.5">
                  Base {money(subscriptionData.base_price_per_cycle)} + AI Add-on {money(subscriptionData.ai_addon_price_per_cycle)}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            disabled={subscriptionActionBusy}
            onClick={openRenewDialog}
            className="ml-auto inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-semibold px-5 py-2.5 rounded-xl shadow-sm shadow-primary-600/25 transition-colors shrink-0"
          >
            <CreditCard className="w-4 h-4" />
            {isActive ? "Renew Now" : "Pay Now"}
          </button>
        </div>

        <div className="relative mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="rounded-xl bg-secondary-50 p-4">
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-secondary-400 mb-2">
              <Users className="w-3.5 h-3.5" /> Seats
            </div>
            <div className="text-sm font-semibold text-secondary-900 mb-2">
              {subscriptionData.seats_used} / {subscriptionData.seat_limit ?? "Unlimited"}
            </div>
            {seatPct !== null && (
              <div className="h-1.5 rounded-full bg-secondary-200 overflow-hidden">
                <div
                  className={`h-full rounded-full ${seatPct >= 100 ? "bg-red-500" : seatPct >= 80 ? "bg-amber-500" : "bg-primary-500"}`}
                  style={{ width: `${seatPct}%` }}
                />
              </div>
            )}
          </div>
          <div className="rounded-xl bg-secondary-50 p-4">
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-secondary-400 mb-2">
              <Calendar className="w-3.5 h-3.5" /> Valid Until
            </div>
            <div className="text-sm font-semibold text-secondary-900">{subscriptionData.end_date}</div>
            {upcomingPeriods.length > 0 && (
              <div className="mt-1 text-xs text-emerald-700">Paid until {paidUntil}</div>
            )}
          </div>
        </div>
        {upcomingPeriods.length > 0 && (
          <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
            <div className="font-semibold">Already paid for</div>
            <ul className="mt-1 space-y-0.5">
              {upcomingPeriods.map((p) => (
                <li key={p.id}>
                  {p.start_date} to {p.end_date} ({fmtCycle(p.billing_cycle)}{p.ai_addon_enabled ? ", with AI Features" : ""})
                </li>
              ))}
            </ul>
            <div className="mt-1 text-xs text-emerald-800">
              Each one starts automatically when the one before it ends.
            </div>
          </div>
        )}
      </Card>

      {/* AI Features */}
      <Card className="p-6">
        {/* flex-wrap, not a viewport breakpoint - same reasoning as the
            Current Plan card above. */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${aiActive ? "bg-violet-100" : "bg-secondary-100"}`}>
              <Sparkles className={`w-6 h-6 ${aiActive ? "text-violet-600" : "text-secondary-400"}`} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-bold text-secondary-900">AI Features</span>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${aiActive ? "bg-violet-100 text-violet-700" : "bg-secondary-100 text-secondary-500"}`}>
                  {aiActive ? <CheckCircle2 className="w-3 h-3" /> : null}
                  {aiActive ? "Enabled" : "Disabled"}
                </span>
              </div>
              <p className="text-sm text-secondary-500 mb-3">
                {subscriptionData.ai_addon_enabled
                  ? `${subscriptionData.ai_sessions_per_day_per_seat || 0} AI sessions/day per doctor`
                  : perSessionAi
                    ? `Pay per AI session: ${money(subscriptionData.ai_price_per_session)} each, taken from your payouts. Usage and any dues are shown above.`
                    : "What your doctors/therapists get with this add-on:"}
              </p>
              {!aiActive && pendingAddonDate && (
                <div className="flex items-start gap-2 mb-3 rounded-lg bg-violet-50 border border-violet-100 px-3 py-2 text-xs text-violet-700">
                  <Calendar className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>
                    Already paid for and scheduled to activate automatically on <span className="font-semibold">{pendingAddonDate}</span> (the start of your next cycle) - not usable until then. Use "Enable AI Features" below instead if you want it active right now.
                  </span>
                </div>
              )}
              {!aiActive && (
                <ul className="space-y-1.5">
                  {AI_FEATURE_BENEFITS.map(({ icon: Icon, text }) => (
                    <li key={text} className="flex items-start gap-2 text-sm text-secondary-600">
                      <Icon className="w-4 h-4 text-violet-500 shrink-0 mt-0.5" />
                      {text}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {perSessionAi ? null : subscriptionData.ai_addon_enabled ? (
            <button
              type="button"
              disabled={subscriptionActionBusy}
              onClick={handleDeactivateAddon}
              className="ml-auto inline-flex items-center justify-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 font-semibold px-4 py-2.5 rounded-xl transition-colors shrink-0"
            >
              Disable
            </button>
          ) : (
            <button
              type="button"
              disabled={addonQuoteLoading}
              onClick={handleOpenEnableAddon}
              className="ml-auto inline-flex items-center justify-center gap-2 border border-violet-200 text-violet-700 hover:bg-violet-50 disabled:opacity-50 font-semibold px-4 py-2.5 rounded-xl transition-colors shrink-0"
            >
              {addonQuoteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              Enable AI Features
            </button>
          )}
        </div>
      </Card>

      {/* Payment History */}
      <Card className="overflow-hidden">
        <div className="px-6 py-4 border-b border-secondary-100 flex items-center gap-3">
          <div className="w-9 h-9 bg-secondary-100 rounded-xl flex items-center justify-center">
            <Receipt className="w-4 h-4 text-secondary-500" />
          </div>
          <h3 className="font-bold text-secondary-900">Payment History</h3>
        </div>
        {subscriptionPayments.length === 0 ? (
          <div className="p-10 text-center text-secondary-400 text-sm">No payments yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-secondary-400 text-xs uppercase tracking-wide bg-secondary-50/50">
                  <th className="py-3 px-6 font-semibold">Date</th>
                  <th className="py-3 px-4 font-semibold">Type</th>
                  <th className="py-3 px-4 font-semibold">Amount</th>
                  <th className="py-3 px-4 font-semibold">Status</th>
                  <th className="py-3 px-6 font-semibold text-right">Invoice</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-50">
                {subscriptionPayments.map((p) => (
                  <tr key={p.id} className="hover:bg-secondary-50/40 transition-colors">
                    <td className="py-3 px-6 text-secondary-600">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "-"}</td>
                    <td className="py-3 px-4 text-secondary-900 font-medium">{PAYMENT_PURPOSE_LABELS[p.payment_purpose] || p.payment_purpose}
                      {p.new_billing_cycle && <span className="block text-xs font-normal text-secondary-400">Switched to {fmtCycle(p.new_billing_cycle)}</span>}
                    </td>
                    <td className="py-3 px-4 text-secondary-900 font-semibold">₹{Number(p.amount).toFixed(2)}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[p.status] || "bg-secondary-100 text-secondary-600"}`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="py-3 px-6 text-right">
                      {p.invoice_number && (
                        <button
                          type="button"
                          onClick={() => handleViewInvoice(p.id)}
                          className="inline-flex items-center gap-1.5 text-primary-600 hover:text-primary-700 font-medium"
                        >
                          <Download className="w-3.5 h-3.5" /> Invoice
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Renew/Pay confirmation */}
      {showRenewConfirm && (
        <ConfirmModal
          title={isActive ? "Renew Subscription" : "Activate Subscription"}
          icon={CreditCard}
          iconClass="bg-primary-100 text-primary-600"
          onCancel={() => { setShowRenewConfirm(false); setBundleAiAddonOnRenew(false); }}
          onConfirm={() => {
            if (addonUnavailable) {
              toast.error(`The AI Features add-on isn't offered on the ${fmtCycle(selectedCycle)} cycle.`);
              return;
            }
            doRenewPayment();
          }}
          confirmLabel={isActive ? "Confirm & Renew" : "Confirm & Pay"}
          confirmBusy={subscriptionActionBusy}
        >
          {cycleOptions.length > 1 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-secondary-400 mb-2">Billing cycle</div>
              <div className="grid grid-cols-2 gap-2">
                {cycleOptions.map((o) => {
                  const total = Number(o.base_price || 0) + (includesAddon ? Number(o.ai_addon_price || 0) : 0);
                  const active = o.billing_cycle === selectedCycle;
                  return (
                    <button
                      key={o.billing_cycle}
                      type="button"
                      onClick={() => setSelectedCycle(o.billing_cycle)}
                      className={`text-left rounded-xl border px-3 py-2.5 transition-colors ${active ? "border-primary-500 bg-primary-50 ring-1 ring-primary-500" : "border-secondary-200 hover:border-secondary-300"}`}
                    >
                      <div className="text-sm font-semibold text-secondary-900 capitalize">
                        {fmtCycle(o.billing_cycle)}
                        {o.is_current && <span className="ml-1.5 text-[10px] font-semibold normal-case text-secondary-400">(current)</span>}
                      </div>
                      <div className="text-xs text-secondary-500">{money(total)} / {o.cycle_days} days</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {subscriptionData.seat_limit == null && (
            <p className="text-xs text-secondary-500 rounded-lg bg-secondary-50 border border-secondary-100 px-3 py-2">
              Your plan has custom terms, so renewal continues on your current {fmtCycle(subscriptionData.billing_cycle)} cycle. Contact support to change your billing cycle.
            </p>
          )}

          {subscriptionData.is_trial && (
            <div className="space-y-2">
              <p className="text-sm text-secondary-600">
                Your free trial ends on <span className="font-semibold">{subscriptionData.trial_end}</span> - the plan you pick starts then, so you don't lose any trial days.
              </p>
              <p className="text-sm font-semibold text-secondary-800">How would you like to pay for AI features?</p>
              <label className="flex items-start gap-2.5 rounded-xl border border-secondary-200 p-3 cursor-pointer">
                <input type="radio" name="ai_option" className="mt-1" checked={aiOption === "addon"} onChange={() => setAiOption("addon")} />
                <span className="text-sm text-secondary-700">
                  <span className="font-semibold">Monthly AI add-on</span> - a fixed price each cycle ({money(renewAddon)}), with a daily AI allowance.
                </span>
              </label>
              <label className="flex items-start gap-2.5 rounded-xl border border-secondary-200 p-3 cursor-pointer">
                <input type="radio" name="ai_option" className="mt-1" checked={aiOption === "per_session"} onChange={() => setAiOption("per_session")} />
                <span className="text-sm text-secondary-700">
                  <span className="font-semibold">Pay per AI session</span> - ₹50 per session, taken from your payouts. Pay only for what you use.
                </span>
              </label>
            </div>
          )}

          <p className="text-sm text-secondary-600">
            {subscriptionData.is_trial ? null : isSwitchingCycle ? (
              <>
                This switches you from {fmtCycle(subscriptionData.billing_cycle)} to <span className="font-semibold">{fmtCycle(selectedCycle)}</span> billing, starting {renewalCycleStart}
                {renewalCycleStart > todayISO ? " (when your current cycle ends - you keep every day already paid for)" : ""}.
                {" "}Priced at the standard {fmtCycle(selectedCycle)} rate for {selectedOption.seats} seat{selectedOption.seats === 1 ? "" : "s"}.
              </>
            ) : (
              <>This renews your {fmtCycle(subscriptionData.billing_cycle)} cycle{subscriptionData.ai_addon_enabled ? ", including your active AI Features add-on" : ""}.</>
            )}
          </p>

          {!subscriptionData.ai_addon_enabled && !pendingAddonDate && !perSessionAi && (
            <label className="flex items-start gap-2.5 rounded-xl border border-violet-100 bg-violet-50/60 p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={bundleAiAddonOnRenew}
                onChange={(e) => setBundleAiAddonOnRenew(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-violet-600"
              />
              <span className="text-sm text-secondary-700">
                <span className="font-semibold text-violet-700">Also enable AI Features with this renewal</span>
                {" "}({money(renewAddon)}/{fmtCycle(selectedCycle || subscriptionData.billing_cycle)} on top of base)
              </span>
            </label>
          )}

          <div className="rounded-xl bg-secondary-50 p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-secondary-500">Base plan</span>
              <span className="font-medium text-secondary-900">{money(renewBase)}</span>
            </div>
            {includesAddon && (
              <div className="flex justify-between text-sm">
                <span className="text-secondary-500">AI Features add-on</span>
                <span className="font-medium text-secondary-900">{addonUnavailable ? "Not offered on this cycle" : money(renewAddon)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-secondary-200">
              <span className="font-bold text-secondary-900">Payable now</span>
              <span className="font-bold text-primary-600 text-lg">{money(renewAmountWithBundle)}</span>
            </div>
          </div>

          {bundleWillDefer && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-800">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>
                You're renewing before your current cycle ends on {subscriptionData.end_date}, so AI Features won't be usable immediately - it'll activate automatically on <span className="font-semibold">{renewalCycleStart}</span>, once the new cycle you're paying for actually starts. If you want AI usable right now instead, close this and use "Enable AI Features" on the card above (charged separately, prorated for the days left in your current cycle).
              </span>
            </div>
          )}
        </ConfirmModal>
      )}

      {/* Enable AI add-on confirmation */}
      {showAddonConfirm && addonQuote && (
        <ConfirmModal
          title="Enable AI Features"
          icon={Sparkles}
          iconClass="bg-violet-100 text-violet-600"
          onCancel={() => setShowAddonConfirm(false)}
          onConfirm={doActivateAddon}
          confirmLabel="Confirm & Pay"
          confirmBusy={subscriptionActionBusy}
        >
          <ul className="space-y-1.5">
            {AI_FEATURE_BENEFITS.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-start gap-2 text-sm text-secondary-600">
                <Icon className="w-4 h-4 text-violet-500 shrink-0 mt-0.5" />
                {text}
              </li>
            ))}
          </ul>
          <div className="rounded-xl bg-violet-50 border border-violet-100 p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-violet-700">Payable now (prorated, {addonQuote.days_remaining} day{addonQuote.days_remaining === 1 ? "" : "s"} left in this cycle)</span>
              <span className="font-bold text-violet-900">{money(addonQuote.amount)}</span>
            </div>
          </div>
          {ongoingTotalWithAddon != null && (
            <p className="text-xs text-secondary-500">
              The base plan for the days already remaining this cycle is already paid for, so only the add-on's prorated share is due now.
              From your next renewal ({subscriptionData.end_date}) onward, your subscription total will be <span className="font-semibold text-secondary-700">{money(ongoingTotalWithAddon)}/{fmtCycle(subscriptionData.billing_cycle)}</span> (base {money(subscriptionData.base_price_per_cycle)} + add-on {money(addonOngoingPrice)}).
            </p>
          )}
        </ConfirmModal>
      )}
    </div>
  );
}
